import { useRef, useMemo, useState, DragEvent } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { UploadIcon } from "@heroicons/react/outline";
import { useRagDocuments } from "../hooks/useRagDocuments";
import RagDocumentRow from "./RagDocumentRow";
import DocumentLibraryPicker from "./DocumentLibraryPicker";
import { SearchInput } from "src/common/components/SearchInput";
import ModalConfirmation from "src/common/components/Modal/ModalConfirmation";
import {
  openConfirmationModal,
  closeConfirmationModal,
} from "src/common/state/slice/modal/modalSlice";
import { setAlert } from "src/features/alert/state/alertsSlice";
import EmptyState from "src/features/statsNew/components/EmptyState";
import {
  RAG_ACCEPTED_EXTENSIONS,
  ragUploadErrorMessage,
  validateRagUpload,
} from "../uploadFormats";

const UNASSIGN_DOC_MODAL_ID = "unassign-rag-document-confirmation";

interface RagDocumentManagerProps {
  agentId: number | null;
  /** Pass hook data from parent to avoid duplicate hook instances */
  hookData?: ReturnType<typeof useRagDocuments>;
}

const RagDocumentManager = ({ agentId, hookData }: RagDocumentManagerProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const internalHook = useRagDocuments(hookData ? null : agentId);
  const { documents, loading, uploading, upload, unassignDoc, reload } = hookData || internalHook;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [docSearch, setDocSearch] = useState("");
  const [pendingUnassignId, setPendingUnassignId] = useState<number | null>(null);

  const filteredDocuments = useMemo(() => {
    if (!docSearch.trim()) return documents;
    const q = docSearch.toLowerCase();
    return documents.filter((d) => d.filename.toLowerCase().includes(q));
  }, [documents, docSearch]);

  const validateAndUpload = async (file: File) => {
    setError(null);

    const rejection = validateRagUpload(file);
    if (rejection) {
      setError(ragUploadErrorMessage(rejection, t));
      return;
    }

    try {
      const doc = await upload(file);
      if (doc?.deduplicated) {
        dispatch(setAlert({ type: "success", msg: t("pages.agents.documentDeduplicated") }));
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      if (msg.includes("Document limit")) {
        setError(t("pages.agents.quotaDocsExceeded"));
      } else if (msg.includes("Storage limit")) {
        setError(t("pages.agents.quotaStorageExceeded"));
      } else {
        setError(t("pages.agents.uploadErrorGeneric"));
      }
    }
  };

  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    files.forEach((file) => validateAndUpload(file));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    files.forEach((file) => validateAndUpload(file));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUnassignRequest = (docId: number) => {
    setPendingUnassignId(docId);
    dispatch(openConfirmationModal(UNASSIGN_DOC_MODAL_ID));
  };

  const handleUnassignConfirm = async () => {
    if (pendingUnassignId === null) return;
    try {
      await unassignDoc(pendingUnassignId);
      dispatch(setAlert({ type: "success", msg: t("pages.agents.documentUnassigned") }));
    } catch {
      setError(t("pages.agents.deleteDocumentError"));
    }
    setPendingUnassignId(null);
    dispatch(closeConfirmationModal(UNASSIGN_DOC_MODAL_ID));
  };

  if (!agentId) return null;

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          {t("pages.agents.documents")}
        </h3>
        {/* Attach from library */}
        <DocumentLibraryPicker
          agentId={agentId}
          currentDocumentIds={documents.map((d) => d.id)}
          onAttached={reload}
        />
      </div>

      {/* Upload zone */}
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
        aria-label={t("pages.agents.uploadDocument")}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click();
        }}
        className={`flex flex-col items-center justify-center p-4 border-2 border-dashed rounded-xl cursor-pointer transition-all duration-200 mb-2 ${
          isDragging
            ? "border-green-500 bg-green-50"
            : "border-gray-300 hover:border-green-400 hover:bg-gray-50 bg-white"
        }`}
      >
        <div className={`p-2 rounded-lg mb-1.5 ${isDragging ? "bg-green-100" : "bg-gray-100"}`}>
          <UploadIcon className={`h-5 w-5 ${isDragging ? "text-green-600" : "text-gray-400"}`} />
        </div>
        <span className="text-xs text-gray-500 text-center">
          {uploading ? (
            <span className="flex items-center gap-2">
              <span className="inline-block w-3 h-3 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
              {t("pages.agents.processing")}
            </span>
          ) : (
            t("pages.agents.uploadHint")
          )}
        </span>
        {/* Library hint */}
        <span className="text-xs text-gray-400 mt-1">
          {t("pages.agents.attachFromLibraryHint")}
        </span>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={RAG_ACCEPTED_EXTENSIONS}
        multiple
        onChange={handleFileSelect}
        className="hidden"
        aria-hidden="true"
      />

      {/* Error display */}
      <div className="h-5 mb-1">
        {error && (
          <p className="text-xs text-red-600 flex items-center gap-1" role="alert">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3.5 w-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {error}
          </p>
        )}
      </div>

      {/* Search documents */}
      {documents.length > 0 && (
        <div className="mb-3">
          <SearchInput
            value={docSearch}
            onChange={setDocSearch}
            placeholder={t("general.search") + "..."}
          />
        </div>
      )}

      {/* Document list */}
      <div className="flex-1 overflow-y-auto rounded-xl border border-gray-200 bg-white">
        {/* Table header */}
        <div className="sticky top-0 bg-gray-50 border-b border-gray-200 px-3 py-2">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            {t("pages.agents.documentCount", { count: filteredDocuments.length })}
          </span>
        </div>

        {loading && documents.length === 0 ? (
          <div className="p-6 flex flex-col items-center gap-2">
            <div className="w-6 h-6 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : documents.length === 0 ? (
          <div className="py-6">
            <EmptyState icon="folder" title={t("pages.agents.noDocuments")} />
          </div>
        ) : filteredDocuments.length === 0 ? (
          <EmptyState
            icon="search"
            title={t("statsNew.emptyState.noSearchResults")}
            description={t("statsNew.emptyState.noSearchResultsDescription")}
          />
        ) : (
          filteredDocuments.map((doc) => (
            <RagDocumentRow
              key={doc.id}
              document={doc}
              onUnassign={handleUnassignRequest}
              currentAgentId={agentId}
            />
          ))
        )}
      </div>

      {/* Unassign confirmation modal */}
      <ModalConfirmation
        id={UNASSIGN_DOC_MODAL_ID}
        title={t("pages.agents.removeFromAgent")}
        text={t("pages.agents.removeFromAgentConfirm")}
        onClickSubmit={handleUnassignConfirm}
      />
    </div>
  );
};

export default RagDocumentManager;
